import React from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  SimpleGrid,
  useColorModeValue,
  Flex,
  Icon,
} from '@chakra-ui/react';
import { FaLightbulb, FaHeart, FaGlobeAmericas } from 'react-icons/fa';
import { useLanguage } from '../contexts/LanguageContext';
import SEO from '../components/SEO';
import { seoData } from '../utils/seoData';

const ValueCard = ({ icon, title, description }) => {
  return (
    <VStack
      bg={useColorModeValue('white', 'gray.800')}
      p={10}
      rounded="3xl"
      shadow="xl"
      align="flex-start"
      spacing={5}
      transition="all 0.4s"
      _hover={{ transform: 'translateY(-8px)', shadow: '2xl' }}
      border="1px"
      borderColor={useColorModeValue('gray.100', 'gray.700')}
    >
      <Flex
        w={16}
        h={16}
        align="center"
        justify="center"
        color="white"
        rounded="2xl"
        bgGradient="linear(to-br, #FF6A5F, #A63FF5)"
      >
        {icon}
      </Flex>
      <Heading size="lg" fontWeight="bold">{title}</Heading>
      <Text 
        color="gray.600" 
        fontSize="md" 
        lineHeight="1.7"
        wordBreak="keep-all"
        style={{
          wordBreak: 'keep-all',
          overflowWrap: 'break-word'
        }}
      >
        {description}
      </Text>
    </VStack>
  );
};

const About = () => {
  const { language } = useLanguage();
  const seo = seoData.about[language] || seoData.about.en;

  const translations = {
    en: {
      heroTitle: 'About Us',
      heroSubtitle:
        "We're on a mission to transform education through innovative AI technology, making quality learning accessible to everyone, everywhere.",
      storyTitle: 'Our Story',
      storyParagraphs: [
        'Founded in 2025, Aidu emerged from a simple observation by a team from KAIST and UC Berkeley: educators needed better tools to create and manage educational content. Traditional methods were time-consuming and limited in their ability to personalize learning experiences.',
        'We brought together experts in AI, education, and software development to build a platform that empowers teachers and delights students. Today, our solutions are used by institutions worldwide, helping millions of learners reach their full potential.',
      ],
      valuesHeading: 'Our Values',
      valuesDescription: 'The principles that guide everything we do',
      values: [
        {
          title: 'Innovation',
          description:
            'Cutting-edge solutions solving real problems in education.',
          icon: FaLightbulb,
        },
        {
          title: 'Empathy',
          description:
            'Understanding user needs to create easier and enjoyable experiences.',
          icon: FaHeart,
        },
        {
          title: 'Accessibility',
          description:
            'Breaking down barriers to make quality learning accessible to all.',
          icon: FaGlobeAmericas,
        },
      ],
      stats: [
        { value: '10K+', label: 'Active Users' },
        { value: '5+', label: 'Institutions' },
        { value: '1M+', label: 'Content Created' },
        { value: '3+', label: 'Countries' },
      ],
    },
    ko: {
      heroTitle: '회사 소개',
      heroSubtitle:
        '혁신적인 AI 기술로 교육을 변화시키고, 누구나 어디에서나 수준 높은 학습을 경험할 수 있도록 돕습니다.',
      storyTitle: '우리의 이야기',
      storyParagraphs: [
        '에이듀는 2025년, 카이스트, UC 버클리 출신 팀이 교육 콘텐츠를 만들고 관리할 때 더 나은 도구가 필요하다는 깨달음에서 출발했습니다. 기존 방식은 시간도 많이 들고 학습 경험을 개인화하기도 어려웠습니다.',
        '우리는 AI, 교육, 소프트웨어 전문가들이 모여 교사를 지원하고 학습자를 만족시키는 플랫폼을 만들었습니다. 오늘날 전 세계 교육 기관이 우리의 솔루션을 활용하여 수백만 학습자의 잠재력을 끌어올리고 있습니다.',
      ],
      valuesHeading: '핵심 가치',
      valuesDescription: '우리가 하는 모든 일의 기준이 되는 원칙들입니다.',
      values: [
        {
          title: '혁신',
          description: '교육 현장의 실제 문제를 해결하는 최첨단 솔루션.',
          icon: FaLightbulb,
        },
        {
          title: '공감',
          description: '사용자 필요를 이해하고 더 쉽고 즐거운 경험 제공.',
          icon: FaHeart,
        },
        {
          title: '접근성',
          description: '모두가 양질의 교육을 누릴 수 있도록 장벽 제거.',
          icon: FaGlobeAmericas,
        },
      ],
      stats: [
        { value: '1K+', label: '활성 사용자' },
        { value: '5+', label: '사용 기관' },
        { value: '1M+', label: '생성된 콘텐츠' },
        { value: '5+', label: '서비스 국가' },
      ],
    },
  };

  const t = translations[language] || translations.en;
  return (
    <>
      <SEO
        title={seo.title}
        description={seo.description}
        keywords={seo.keywords}
        canonical="/about"
        lang={language}
      />
      <Box>
        {/* Hero Section */}
      <Box 
        bgGradient="linear(to-br, rgba(255, 106, 95, 0.05), rgba(166, 63, 245, 0.05))"
        py={{ base: 20, md: 28 }}
      >
        <Container maxW="container.xl">
          <VStack spacing={8} textAlign="center">
            <Heading 
              fontSize={{ base: '4xl', md: '5xl', lg: '6xl' }}
              fontWeight="bold"
            >
              {t.heroTitle}
            </Heading>
            <Text fontSize={{ base: 'lg', md: 'xl' }} color="gray.600" maxW="900px" lineHeight="tall">
              {t.heroSubtitle}
            </Text>
          </VStack>
        </Container>
      </Box>

      {/* Story Section */}
      <Box py={24}>
        <Container maxW="container.xl">
          <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={20} alignItems="center">
            <VStack align="flex-start" spacing={8}>
              <Heading fontSize={{ base: '3xl', md: '4xl' }} fontWeight="bold">
                {t.storyTitle}
              </Heading>
              {t.storyParagraphs.map((paragraph, index) => (
                <Text key={index} fontSize="lg" color="gray.600" lineHeight="tall">
                  {paragraph}
                </Text>
              ))}
            </VStack>
            <Box
              bgGradient="linear(135deg, #FF6A5F, #A63FF5, #C56CFF, #FF8FA5)"
              h={{ base: '300px', md: '450px' }}
              rounded="3xl"
              display="flex"
              alignItems="center"
              justifyContent="center"
              position="relative"
              overflow="hidden"
            >
              <Icon 
                as={FaLightbulb} 
                w={{ base: 32, md: 48 }} 
                h={{ base: 32, md: 48 }} 
                color="white" 
                opacity={0.9}
                style={{ filter: 'drop-shadow(0 20px 40px rgba(0,0,0,0.2))' }}
              />
            </Box>
          </SimpleGrid>
        </Container>
      </Box>

      {/* Values Section */}
      <Box py={24} bg={useColorModeValue('gray.50', 'gray.900')}>
        <Container maxW="container.xl">
          <VStack spacing={16}>
            <VStack spacing={6} textAlign="center">
              <Heading fontSize={{ base: '3xl', md: '4xl' }} fontWeight="bold">
                {t.valuesHeading}
              </Heading>
              <Text fontSize="xl" color="gray.600" maxW="700px" lineHeight="tall">
                {t.valuesDescription}
              </Text>
            </VStack>

            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={10} w="full">
              {t.values.map((value, index) => (
                <ValueCard
                  key={index}
                  icon={<Icon as={value.icon} w={8} h={8} />}
                  title={value.title}
                  description={value.description}
                />
              ))}
            </SimpleGrid>
          </VStack>
        </Container>
      </Box>

      {/* Stats Section */}
      <Box 
        py={24}
        bgGradient="linear(to-br, rgba(255, 106, 95, 0.05), rgba(166, 63, 245, 0.05))"
      >
        <Container maxW="container.xl">
          <SimpleGrid columns={{ base: 1, sm: 2, md: 4 }} spacing={12}>
            {t.stats.map((stat, index) => (
              <VStack key={index} spacing={4}>
                <Heading
                  fontSize={{ base: '5xl', md: '6xl' }}
                  fontWeight="bold"
                  bgGradient={
                    index === 0
                      ? 'linear(to-r, #FF6A5F, #A63FF5)'
                      : index === 1
                      ? 'linear(to-r, #A63FF5, #C56CFF)'
                      : index === 2
                      ? 'linear(to-r, #C56CFF, #FF8FA5)'
                      : 'linear(to-r, #FF8FA5, #FF6A5F)'
                  }
                  bgClip="text"
                >
                  {stat.value}
                </Heading>
                <Text fontSize="xl" fontWeight="semibold" color="gray.700">
                  {stat.label}
                </Text>
              </VStack>
            ))}
          </SimpleGrid>
        </Container>
      </Box>
      </Box>
    </>
  );
};

export default About;

